/**
 * 系统水印图片生成（与 `@home/components/Mask` 同源）
 *
 * 仅负责生成可平铺的 pattern Data URL，不依赖 React。
 * 页面遮罩请用 Mask 组件；导出 HTML/PDF 请用本工具 + `@home/utils/htmlExport`。
 *
 * @module @home/utils/watermark
 */

export interface IWatermarkConfig {
  text?: string;
  marginLeft?: number;
  marginTop?: number;
  degree?: number;
  scale?: number;
  font?: string;
  fontSize?: number;
  color?: string;
  waterWidth?: number;
  waterHeight?: number;
  loginCode?: string;
  lineHeight?: number;
}

const CACHE: Record<string, string> = {};

/**
 * 构建水印图片 Data URL（无缓存）
 */
function buildImgDataUrl(config: IWatermarkConfig): string {
  const {
    text = '',
    marginLeft = 0,
    marginTop = 0,
    degree = 0,
    scale = 1,
    font = 'Roboto Regular',
    fontSize = 24,
    color = 'rgba(0, 0, 0, 0.08)',
    waterWidth = 430,
    waterHeight = 280,
    loginCode = '',
    lineHeight = 30,
  } = config;

  const canvas = document.createElement('canvas');
  canvas.width = waterWidth * scale;
  canvas.height = waterHeight * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }

  ctx.fillStyle = color;
  ctx.font = `${fontSize * scale}px ${font}`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  ctx.save();
  ctx.translate(scale * marginLeft, scale * marginTop);
  ctx.rotate((degree * Math.PI) / 180);
  ctx.fillText(text, 0, 0);
  if (loginCode) {
    ctx.fillText(loginCode, 0, scale * lineHeight);
  }
  ctx.restore();

  return canvas.toDataURL();
}

/**
 * 生成水印图片 Data URL（带缓存）
 *
 * 默认样式与 Mask 一致：-30°、`rgba(106,109,126,0.08)`、430×280 平铺单元。
 *
 * @example
 * ```ts
 * genWatermarkDataUrl({
 *   text: 'userid@DataBrain',
 *   loginCode: '12345',
 * });
 * ```
 */
export function genWatermarkDataUrl(props: IWatermarkConfig = {}): string {
  const {
    text = 'hello world',
    marginLeft = 20,
    marginTop = 220,
    degree = -30,
    scale = 1,
    font = 'Roboto Regular,-apple-system,PingFang SC,microsoft yahei ui,microsoft yahei,sans-serif',
    fontSize = 24,
    color = 'rgba(106, 109, 126, 0.08)',
    waterWidth = 430,
    waterHeight = 280,
    loginCode = '',
    lineHeight = 30,
  } = props;

  const key = [
    text,
    marginLeft,
    marginTop,
    degree,
    scale,
    font,
    fontSize,
    color,
    waterWidth,
    waterHeight,
    loginCode,
    lineHeight,
  ].join('|');

  if (!CACHE[key]) {
    CACHE[key] = buildImgDataUrl({
      text,
      marginLeft,
      marginTop,
      degree,
      scale,
      font,
      fontSize,
      color,
      waterWidth,
      waterHeight,
      loginCode,
      lineHeight,
    });
  }

  return CACHE[key];
}

/**
 * 根据用户信息生成系统默认水印文案对应的 Data URL
 * 文案规则与 Mask 一致：`{openid|userid}@DataBrain` + code
 */
export function genUserWatermarkDataUrl(
  userInfo?: {
    openid?: string;
    userid?: string;
    code?: string | number;
  } | null,
  color?: string,
): string {
  if (!userInfo) {
    return '';
  }
  const userId = userInfo.openid || userInfo.userid || '';
  const loginCode = userInfo.code ? `${userInfo.code}` : '';
  return genWatermarkDataUrl({
    text: `${userId}@DataBrain`,
    loginCode,
    color,
  });
}
